import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { MediaPicker } from "@/components/admin/MediaPicker";
import { AdminImageInput } from "@/components/admin/AdminImageInput";
import { SectionRenderer } from "@/components/sections/SectionRenderer";
import { RichTextField } from "@/components/editor/RichTextField";
import { Loader } from "@/components/common/Loader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, ArrowUp, ArrowDown, Copy, Trash2, Eye, EyeOff, GripVertical, Plus,
  Save, Send, Undo2, History, Image as ImageIcon, Monitor, Tablet, Smartphone, RotateCcw, Users, ExternalLink,
} from "lucide-react";
import { WIDGET_MANAGERS } from "@/components/sections/widgetRegistry";
import { WIDGET_EDITABLE, hasEditableChrome, WIDGET_ITEMS, hasEditableItems } from "@/components/sections/widgetEditable";

const SECTION_TYPE_LABEL = {
  hero: "Hero", rich_text: "Text", image_text: "Image + Text", cta_band: "CTA Band",
  feature_grid: "Feature Grid", gallery: "Gallery", embed: "Functional", carousel_banner: "Carousel Banner",
};

// A sensible starter slide for new carousel banners.
const newCarouselSlide = () => ({
  eyebrow: "Featured",
  heading: "Ready To Train With Purpose?",
  body: "Book a free intro workout and meet a coach today.",
  media: { image_url: "", video_url: "", alt: "", focal_x: 50, focal_y: 50 },
  overlay: { type: "red", opacity: 55 },
  buttons: [{ label: "Book Intro", href: "/booking", style: "light" }],
});
const OVERLAY_OPTS = [
  { value: "none", label: "None" },
  { value: "black", label: "Solid Black" },
  { value: "red", label: "Solid Red" },
  { value: "black_ltr", label: "Black Left → Right" },
];
const BTN_STYLES = [
  { value: "primary", label: "Primary (Red)" },
  { value: "secondary", label: "Secondary (Outline)" },
  { value: "light", label: "Light (White)" },
];
const VIEWPORTS = { desktop: 1280, tablet: 768, phone: 390 };
const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(16).slice(2)}`);

// Compute the overlay preview style without a nested ternary.
const overlayStyle = (type, opacity) => {
  const op = Math.max(0, Math.min(100, opacity ?? 0)) / 100;
  switch (type) {
    case "black": return { backgroundColor: `rgba(0,0,0,${op})` };
    case "red": return { backgroundColor: `rgba(225,29,56,${op})` };
    case "black_ltr": return { backgroundImage: `linear-gradient(to right, rgba(0,0,0,${op}) 0%, rgba(0,0,0,0) 100%)` };
    default: return {};
  }
};

const newSection = (type) => ({
  id: uid(), type, label: SECTION_TYPE_LABEL[type] || "Section", visible: true,
  content: type === "hero"
    ? { eyebrow: "", heading_left: "New ", heading_right: "Section", subhead: "" }
    : type === "carousel_banner"
      ? { slides: [newCarouselSlide()], interval: 6000 }
      : { heading: "New Section", body: "" },
  media: { image_url: "", alt: "", focal_x: 50, focal_y: 50, video_url: "" },
  overlay: { type: type === "hero" || type === "cta_band" ? "black" : "none", opacity: 50 },
  buttons: [], widget: "",
});

export default function AdminPageEditor() {
  const { key } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [page, setPage] = useState(null);
  const [sections, setSections] = useState([]);
  const [baseVersion, setBaseVersion] = useState(1);
  const [selected, setSelected] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [viewport, setViewport] = useState("desktop");
  const [revisionsOpen, setRevisionsOpen] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/pages/${key}`).then((r) => {
      setPage(r.data);
      const base = r.data.draft_sections ?? r.data.published_sections ?? [];
      setSections(JSON.parse(JSON.stringify(base)));
      setBaseVersion(r.data.version || 1);
      setDirty(false);
      setSelected(0);
    }).catch(() => toast.error("Could not load page")).finally(() => setLoading(false));
  }, [key]);
  useEffect(() => { load(); }, [load]);

  const mutate = (fn) => { setSections((prev) => { const copy = JSON.parse(JSON.stringify(prev)); fn(copy); return copy; }); setDirty(true); };
  const patchSection = (idx, patch) => mutate((s) => { s[idx] = { ...s[idx], ...patch }; });
  const patchNested = (idx, group, patch) => mutate((s) => { s[idx][group] = { ...(s[idx][group] || {}), ...patch }; });

  // Anchor helpers: keep anchor IDs lowercase/url-safe and unique per page.
  const cleanAnchor = (v) => String(v || "").toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/-{2,}/g, "-");
  const anchorDuplicate = (idx, val) => !!val && sections.some((s, i) => i !== idx && (s.anchor || "").trim() === val);
  const anchorIssues = sections
    .map((s, i) => ({ i, anchor: (s.anchor || "").trim(), label: s.label || SECTION_TYPE_LABEL[s.type] }))
    .filter((x) => !x.anchor || anchorDuplicate(x.i, x.anchor));

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= sections.length) return;
    mutate((s) => { const [it] = s.splice(idx, 1); s.splice(j, 0, it); });
    setSelected(j);
  };
  const duplicate = (idx) => { mutate((s) => { const c = JSON.parse(JSON.stringify(s[idx])); c.id = uid(); c.label = `${c.label} (copy)`; s.splice(idx + 1, 0, c); }); setSelected(idx + 1); };
  const remove = (idx) => { mutate((s) => s.splice(idx, 1)); setSelected((p) => Math.max(0, p > idx ? p - 1 : p)); };
  const addSection = (type) => { mutate((s) => s.push(newSection(type))); setSelected(sections.length); };

  const onDrop = (idx) => {
    if (dragIndex === null || dragIndex === idx) return;
    mutate((s) => { const [it] = s.splice(dragIndex, 1); s.splice(idx, 0, it); });
    setSelected(idx); setDragIndex(null);
  };

  const saveDraft = async () => {
    setSaving(true);
    try { await api.put(`/admin/pages/${key}/draft`, { sections }); toast.success("Draft saved"); setDirty(false); setPage((p) => ({ ...p, has_draft: true })); }
    catch (e) { toast.error("Could not save draft"); }
    finally { setSaving(false); }
  };

  const commit = async () => {
    setSaving(true);
    try {
      if (dirty) await api.put(`/admin/pages/${key}/draft`, { sections });
      const { data } = await api.post(`/admin/pages/${key}/commit`, { base_version: baseVersion, message: "" });
      toast.success(`Published live (v${data.version})`);
      load();
    } catch (e) {
      if (e?.response?.status === 409) toast.error("This page was changed by someone else. Reload before committing.", { duration: 6000 });
      else if (e?.response?.status === 400) toast.error("Save a draft change first, then commit.");
      else toast.error(e?.response?.data?.detail || "Commit failed — live page unchanged.");
    } finally { setSaving(false); }
  };

  const discard = async () => {
    try { await api.post(`/admin/pages/${key}/discard`); toast.success("Draft discarded"); load(); }
    catch (e) { toast.error("Could not discard draft"); }
  };

  const openRevisions = () => {
    setRevisionsOpen(true);
    api.get(`/admin/pages/${key}/revisions`).then((r) => setRevisions(r.data)).catch(() => setRevisions([]));
  };
  const restore = async (revId) => {
    try { await api.post(`/admin/pages/${key}/restore/${revId}`); toast.success("Revision loaded into draft"); setRevisionsOpen(false); load(); }
    catch (e) { toast.error("Restore failed"); }
  };

  const sec = sections[selected];

  const previewSections = useMemo(() => sections, [sections]);

  if (loading) return <AdminLayout><Loader /></AdminLayout>;

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/admin/pages" className="flex h-9 w-9 items-center justify-center rounded-md border border-border hover:bg-accent transition-colors" data-testid="editor-back"><ArrowLeft className="h-4 w-4" /></Link>
          <div>
            <h1 className="display-title text-3xl">{page?.name}</h1>
            <p className="text-xs text-muted-foreground">{page?.url} · v{baseVersion} {page?.has_draft && <span className="ml-1 rounded bg-[hsl(48_96%_53%)] px-1.5 py-0.5 text-[10px] font-semibold uppercase text-black">Unpublished draft</span>}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setPreviewOpen(true)} data-testid="editor-preview-btn" className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase hover:bg-accent transition-colors"><Eye className="h-4 w-4" /> Preview</button>
          <button onClick={openRevisions} data-testid="editor-revisions-btn" className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase hover:bg-accent transition-colors"><History className="h-4 w-4" /> Revisions</button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="editor-discard-btn" disabled={!page?.has_draft && !dirty} className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase hover:bg-accent transition-colors disabled:opacity-50"><Undo2 className="h-4 w-4" /> Discard</button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Discard draft changes?</AlertDialogTitle><AlertDialogDescription>This reverts the draft to the live published version. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={discard} className="bg-destructive hover:bg-destructive/90">Discard</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <button onClick={saveDraft} disabled={saving} data-testid="editor-save-draft-btn" className="inline-flex items-center gap-1.5 rounded-md border border-[hsl(var(--brand-red))] px-3 py-2 text-xs font-semibold uppercase text-brand hover:bg-brand/10 transition-colors disabled:opacity-50"><Save className="h-4 w-4" /> Save Draft</button>
          <button onClick={commit} disabled={saving} data-testid="editor-commit-btn" className="inline-flex items-center gap-1.5 rounded-md bg-brand px-4 py-2 text-xs font-semibold uppercase text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors disabled:opacity-50"><Send className="h-4 w-4" /> Commit Changes</button>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[320px_1fr]">
        {/* Section list */}
        <div className="space-y-2">
          {anchorIssues.length > 0 && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive" data-testid="anchor-issues-banner">
              <p className="font-semibold uppercase tracking-wide">Anchor IDs need attention</p>
              <ul className="mt-1 list-disc pl-4">
                {anchorIssues.map((x) => <li key={x.i}>{x.label}: {x.anchor ? `"#${x.anchor}" is duplicated` : "missing anchor ID"}</li>)}
              </ul>
            </div>
          )}
          <div className="flex items-center justify-between">
            <h2 className="font-condensed text-sm font-semibold uppercase tracking-wide text-muted-foreground">Sections</h2>
            <Select onValueChange={(v) => addSection(v)} value="">
              <SelectTrigger className="h-8 w-32 text-xs" data-testid="editor-add-section"><span className="inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add</span></SelectTrigger>
              <SelectContent>
                <SelectItem value="rich_text">Text Block</SelectItem>
                <SelectItem value="image_text">Image + Text</SelectItem>
                <SelectItem value="cta_band">CTA Band</SelectItem>
                <SelectItem value="hero">Hero</SelectItem>
                <SelectItem value="gallery">Gallery</SelectItem>
                <SelectItem value="feature_grid">Feature Grid</SelectItem>
                <SelectItem value="carousel_banner">Carousel Banner</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {sections.map((s, i) => (
            <div
              key={s.id}
              draggable
              onDragStart={() => setDragIndex(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(i)}
              onClick={() => setSelected(i)}
              data-testid={`section-row-${i}`}
              className={`flex items-center gap-2 rounded-lg border p-2.5 cursor-pointer transition-colors ${selected === i ? "border-[hsl(var(--brand-red))] bg-brand/5" : "border-border hover:bg-muted/40"} ${s.visible === false ? "opacity-60" : ""}`}
            >
              <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{s.label || SECTION_TYPE_LABEL[s.type]}</p>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{SECTION_TYPE_LABEL[s.type] || s.type}{s.anchor ? ` · #${s.anchor}` : ""}{s.visible === false ? " · hidden" : ""}</p>
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                <button onClick={(e) => { e.stopPropagation(); patchSection(i, { visible: s.visible === false ? true : false }); }} title="Show/Hide" data-testid={`section-visible-${i}`} className="flex h-7 w-7 items-center justify-center rounded hover:bg-accent">{s.visible === false ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</button>
                <button onClick={(e) => { e.stopPropagation(); move(i, -1); }} title="Move up" data-testid={`section-up-${i}`} className="flex h-7 w-7 items-center justify-center rounded hover:bg-accent"><ArrowUp className="h-3.5 w-3.5" /></button>
                <button onClick={(e) => { e.stopPropagation(); move(i, 1); }} title="Move down" data-testid={`section-down-${i}`} className="flex h-7 w-7 items-center justify-center rounded hover:bg-accent"><ArrowDown className="h-3.5 w-3.5" /></button>
                <button onClick={(e) => { e.stopPropagation(); duplicate(i); }} title="Duplicate" data-testid={`section-duplicate-${i}`} className="flex h-7 w-7 items-center justify-center rounded hover:bg-accent"><Copy className="h-3.5 w-3.5" /></button>
                <button onClick={(e) => { e.stopPropagation(); remove(i); }} title="Delete" data-testid={`section-delete-${i}`} className="flex h-7 w-7 items-center justify-center rounded text-destructive hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
          {sections.length === 0 && <p className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">No sections. Add one above.</p>}
        </div>

        {/* Section editor */}
        <div className="rounded-xl border border-border bg-card p-4 sm:p-6" data-testid="section-editor">
          {!sec ? <p className="text-sm text-muted-foreground">Select a section to edit.</p> : (
            <div className="space-y-4">
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground" data-testid="section-breadcrumb">{page?.name || key} <span className="text-brand">›</span> {sec.label || SECTION_TYPE_LABEL[sec.type]}{sec.anchor ? <span className="text-muted-foreground"> (#{sec.anchor})</span> : null}</p>
                <h3 className="display-title text-2xl">{sec.label || SECTION_TYPE_LABEL[sec.type]}</h3>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Section name (admin label)"><Input value={sec.label || ""} onChange={(e) => patchSection(selected, { label: e.target.value })} placeholder={SECTION_TYPE_LABEL[sec.type]} data-testid="section-label" /></Field>
                <AnchorField value={sec.anchor || ""} duplicate={anchorDuplicate(selected, (sec.anchor || "").trim())} onChange={(v) => patchSection(selected, { anchor: cleanAnchor(v) })} onBlur={(v) => patchSection(selected, { anchor: cleanAnchor(v).replace(/^-+|-+$/g, "") })} />
              </div>

              {sec.type === "embed" ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3 pt-1">
                <Switch checked={sec.visible !== false} onCheckedChange={(v) => patchSection(selected, { visible: v })} data-testid="embed-visible-switch" />
                <span className="text-sm">{sec.visible === false ? "Hidden from visitors" : "Visible to visitors"}</span>
              </div>

              {hasEditableChrome(sec.widget) ? (
                <div className="mt-3 rounded-lg border border-border p-4">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand">Section text &amp; images</p>
                  <p className="mb-4 text-xs text-muted-foreground">Edit the wording, images and buttons shown around this section. Leave a field blank to use the built-in default. The list/data inside (e.g. cards) is managed separately below.</p>
                  <EmbedChromeEditor
                    fields={WIDGET_EDITABLE[sec.widget]}
                    content={sec.content || {}}
                    setField={(name, value) => patchNested(selected, "content", { [name]: value })}
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">This is a functional section. Its layout isn't text-editable here, but you can <strong>reorder</strong> or <strong>hide/show</strong> it. To change its data, open its manager below.</p>
              )}

              {hasEditableItems(sec.widget) && (
                <EmbedItemsEditor
                  spec={WIDGET_ITEMS[sec.widget]}
                  items={(sec.content && sec.content.items) || []}
                  mutate={mutate}
                  selected={selected}
                />
              )}

              {WIDGET_MANAGERS[sec.widget] ? (
                <div className="pt-2">
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Manage this content</p>
                  <Link to={WIDGET_MANAGERS[sec.widget].to} data-testid="edit-widget-source-link" className="inline-flex items-center gap-1.5 rounded-md bg-brand px-4 py-2 text-xs font-semibold uppercase text-white transition-colors hover:bg-[hsl(var(--brand-red-deep))]"><ExternalLink className="h-4 w-4" /> {WIDGET_MANAGERS[sec.widget].label}</Link>
                </div>
              ) : !hasEditableChrome(sec.widget) ? (
                <p className="pt-2 text-xs text-muted-foreground">This section has no separate content manager — it uses fixed shortcuts/branding managed by the app.</p>
              ) : null}
            </div>
          ) : (
            <Tabs defaultValue="content" className="w-full">
              <TabsList className="flex flex-wrap">
                <TabsTrigger value="content" data-testid="tab-content">Content</TabsTrigger>
                {(sec.type === "feature_grid" || sec.type === "gallery") && <TabsTrigger value="items" data-testid="tab-items">Items</TabsTrigger>}
                {sec.type === "carousel_banner" && <TabsTrigger value="slides" data-testid="tab-slides">Slides</TabsTrigger>}
                {sec.type !== "carousel_banner" && <TabsTrigger value="media" data-testid="tab-media">Media</TabsTrigger>}
                {sec.type !== "carousel_banner" && <TabsTrigger value="overlay" data-testid="tab-overlay">Overlay</TabsTrigger>}
                {sec.type !== "carousel_banner" && <TabsTrigger value="buttons" data-testid="tab-buttons">Buttons</TabsTrigger>}
                <TabsTrigger value="visibility" data-testid="tab-visibility">Visibility</TabsTrigger>
              </TabsList>

              {/* CONTENT */}
              <TabsContent value="content" className="mt-4 space-y-4">
                {sec.type === "hero" ? (
                  <>
                    <Field label="Eyebrow (small label above title)"><Input value={sec.content.eyebrow || ""} onChange={(e) => patchNested(selected, "content", { eyebrow: e.target.value })} data-testid="content-eyebrow" /></Field>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="Heading — red part"><Input value={sec.content.heading_left || ""} onChange={(e) => patchNested(selected, "content", { heading_left: e.target.value })} data-testid="content-heading-left" /></Field>
                      <Field label="Heading — white part"><Input value={sec.content.heading_right || ""} onChange={(e) => patchNested(selected, "content", { heading_right: e.target.value })} data-testid="content-heading-right" /></Field>
                    </div>
                    <Field label="Subheading"><RichTextField value={sec.content.subhead || ""} onChange={(v) => patchNested(selected, "content", { subhead: v })} testid="content-subhead" /></Field>
                  </>
                ) : sec.type === "carousel_banner" ? (
                  <>
                    <p className="text-sm text-muted-foreground">A rotating banner of full-width slides with a video/image background. Edit slides in the <strong>Slides</strong> tab.</p>
                    <Field label={`Auto-rotate every ${Math.round((sec.content.interval || 6000) / 1000)}s`}>
                      <Slider value={[Math.round((sec.content.interval || 6000) / 1000)]} min={3} max={15} step={1} onValueChange={([v]) => patchNested(selected, "content", { interval: v * 1000 })} data-testid="content-interval" />
                    </Field>
                  </>
                ) : (
                  <>
                    <Field label="Eyebrow"><Input value={sec.content.eyebrow || ""} onChange={(e) => patchNested(selected, "content", { eyebrow: e.target.value })} data-testid="content-eyebrow" /></Field>
                    <Field label="Heading"><Input value={sec.content.heading || ""} onChange={(e) => patchNested(selected, "content", { heading: e.target.value })} data-testid="content-heading" /></Field>
                    {(sec.type === "rich_text" || sec.type === "image_text" || sec.type === "cta_band") && (
                      <Field label="Body text"><RichTextField value={sec.content.body || ""} onChange={(v) => patchNested(selected, "content", { body: v })} testid="content-body" /></Field>
                    )}
                    {sec.type === "image_text" && (
                      <Field label="Image side">
                        <Select value={sec.content.image_side || "left"} onValueChange={(v) => patchNested(selected, "content", { image_side: v })}>
                          <SelectTrigger data-testid="content-image-side"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="left">Left</SelectItem><SelectItem value="right">Right</SelectItem></SelectContent>
                        </Select>
                      </Field>
                    )}
                    {sec.type === "feature_grid" && (
                      <Field label="Subtitle (optional)"><Textarea rows={2} value={sec.content.subtitle || ""} onChange={(e) => patchNested(selected, "content", { subtitle: e.target.value })} data-testid="content-subtitle" /></Field>
                    )}
                    {sec.type === "rich_text" && (
                      <>
                        <Field label="Subtitle (optional)"><Textarea rows={2} value={sec.content.subtitle || ""} onChange={(e) => patchNested(selected, "content", { subtitle: e.target.value })} data-testid="content-subtitle-rt" /></Field>
                        <Field label="Alignment">
                          <Select value={sec.content.align || "left"} onValueChange={(v) => patchNested(selected, "content", { align: v })}>
                            <SelectTrigger data-testid="content-align"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="left">Left</SelectItem><SelectItem value="center">Center</SelectItem></SelectContent>
                          </Select>
                        </Field>
                      </>
                    )}
                    {sec.type === "cta_band" && (
                      <Field label="Band style">
                        <Select value={sec.content.variant || "dark"} onValueChange={(v) => patchNested(selected, "content", { variant: v })}>
                          <SelectTrigger data-testid="content-cta-variant"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="dark">Dark (image / overlay)</SelectItem>
                            <SelectItem value="brand">Solid Brand Red</SelectItem>
                            <SelectItem value="red_image">Red-Tinted Image</SelectItem>
                            <SelectItem value="video_red">Video Window (B&amp;W + red)</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                    )}
                  </>
                )}
                <Field label="Internal label (for editors only)"><Input value={sec.label || ""} onChange={(e) => patchSection(selected, { label: e.target.value })} data-testid="content-label" /></Field>
              </TabsContent>

              {/* ITEMS (feature grid / gallery) */}
              {(sec.type === "feature_grid" || sec.type === "gallery") && (
                <TabsContent value="items" className="mt-4 space-y-3">
                  {sec.type === "feature_grid" && (
                    <>
                      {(sec.content.items || []).map((it, ii) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={ii} className="space-y-2 rounded-lg border border-border p-3" data-testid={`item-row-${ii}`}>
                          <div className="grid gap-2 sm:grid-cols-2">
                            <Field label="Icon (Lucide name)"><Input value={it.icon || ""} onChange={(e) => mutate((s) => { s[selected].content.items[ii].icon = e.target.value; })} data-testid={`item-icon-${ii}`} placeholder="e.g. Dumbbell" /></Field>
                            <Field label="Title"><Input value={it.title || ""} onChange={(e) => mutate((s) => { s[selected].content.items[ii].title = e.target.value; })} data-testid={`item-title-${ii}`} /></Field>
                          </div>
                          <Field label="Text"><Textarea rows={2} value={it.body || ""} onChange={(e) => mutate((s) => { s[selected].content.items[ii].body = e.target.value; })} data-testid={`item-body-${ii}`} /></Field>
                          <Field label="Link (optional)"><Input value={it.link || ""} onChange={(e) => mutate((s) => { s[selected].content.items[ii].link = e.target.value; })} data-testid={`item-link-${ii}`} placeholder="/services or https://…" /></Field>
                          <div className="rounded-lg border border-border p-3">
                            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Card image</Label>
                            <p className="mb-1 mt-0.5 text-[11px] text-muted-foreground">Upload, drag &amp; drop, pick from the library, or generate with AI. Large images are auto-optimized. Leave empty for the clean icon-only card.</p>
                            <AdminImageInput
                              value={it.image || ""}
                              onChange={(v) => mutate((s) => { s[selected].content.items[ii].image = v; })}
                              testid={`item-image-${ii}`}
                            />
                            <Field label="Image alt text (accessibility)"><Input value={it.image_alt || ""} onChange={(e) => mutate((s) => { s[selected].content.items[ii].image_alt = e.target.value; })} data-testid={`item-image-alt-${ii}`} placeholder={it.title || "Describe the image"} /></Field>
                          </div>
                          <button onClick={() => mutate((s) => { s[selected].content.items.splice(ii, 1); })} data-testid={`item-remove-${ii}`} className="text-xs font-semibold uppercase text-destructive hover:underline">Remove item</button>
                        </div>
                      ))}
                      <button onClick={() => mutate((s) => { s[selected].content.items = [...(s[selected].content.items || []), { icon: "Star", title: "New item", body: "" }]; })} data-testid="item-add" className="inline-flex items-center gap-1.5 rounded-md bg-brand px-4 py-2 text-xs font-semibold uppercase text-white transition-colors hover:bg-[hsl(var(--brand-red-deep))]"><Plus className="h-4 w-4" /> Add item</button>
                    </>
                  )}
                  {sec.type === "gallery" && (
                    <>
                      {(sec.content.images || []).map((img, ii) => {
                        const url = typeof img === "string" ? img : (img.url || "");
                        const alt = typeof img === "string" ? "" : (img.alt || "");
                        return (
                          // eslint-disable-next-line react/no-array-index-key
                          <div key={ii} className="space-y-2 rounded-lg border border-border p-3" data-testid={`image-row-${ii}`}>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Image {ii + 1}</Label>
                            <AdminImageInput
                              value={url}
                              onChange={(v) => mutate((s) => { const a = [...(s[selected].content.images || [])]; a[ii] = { url: v, alt }; s[selected].content.images = a; })}
                              testid={`image-${ii}`}
                            />
                            <Field label="Alt text (accessibility)"><Input value={alt} onChange={(e) => mutate((s) => { const a = [...(s[selected].content.images || [])]; a[ii] = { url, alt: e.target.value }; s[selected].content.images = a; })} data-testid={`image-alt-${ii}`} placeholder="Describe the image" /></Field>
                            <div className="flex items-center gap-3">
                              <button onClick={() => mutate((s) => { const a = [...s[selected].content.images]; if (ii > 0) { [a[ii - 1], a[ii]] = [a[ii], a[ii - 1]]; s[selected].content.images = a; } })} disabled={ii === 0} data-testid={`image-up-${ii}`} className="text-xs font-semibold uppercase text-muted-foreground hover:text-foreground disabled:opacity-40">↑ Up</button>
                              <button onClick={() => mutate((s) => { const a = [...s[selected].content.images]; if (ii < a.length - 1) { [a[ii + 1], a[ii]] = [a[ii], a[ii + 1]]; s[selected].content.images = a; } })} disabled={ii === (sec.content.images.length - 1)} data-testid={`image-down-${ii}`} className="text-xs font-semibold uppercase text-muted-foreground hover:text-foreground disabled:opacity-40">↓ Down</button>
                              <button onClick={() => mutate((s) => { s[selected].content.images.splice(ii, 1); })} data-testid={`image-remove-${ii}`} className="text-xs font-semibold uppercase text-destructive hover:underline">Remove image</button>
                            </div>
                          </div>
                        );
                      })}
                      <button onClick={() => mutate((s) => { s[selected].content.images = [...(s[selected].content.images || []), { url: "", alt: "" }]; })} data-testid="image-add" className="inline-flex items-center gap-1.5 rounded-md bg-brand px-4 py-2 text-xs font-semibold uppercase text-white transition-colors hover:bg-[hsl(var(--brand-red-deep))]"><Plus className="h-4 w-4" /> Add image</button>
                    </>
                  )}
                </TabsContent>
              )}

              {/* SLIDES (carousel banner) */}
              {sec.type === "carousel_banner" && (
                <TabsContent value="slides" className="mt-4 space-y-4">
                  {(sec.content.slides || []).map((sl, si) => {
                    const ov = sl.overlay || { type: "red", opacity: 55 };
                    const btns = sl.buttons || [];
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <div key={si} className="space-y-3 rounded-lg border border-border p-3" data-testid={`slide-row-${si}`}>
                        <div className="flex items-center justify-between">
                          <p className="font-condensed text-sm font-semibold uppercase tracking-wide">Slide {si + 1}</p>
                          <button onClick={() => mutate((s) => { s[selected].content.slides.splice(si, 1); })} data-testid={`slide-remove-${si}`} className="text-xs font-semibold uppercase text-destructive hover:underline">Remove slide</button>
                        </div>
                        <Field label="Eyebrow"><Input value={sl.eyebrow || ""} onChange={(e) => mutate((s) => { s[selected].content.slides[si].eyebrow = e.target.value; })} data-testid={`slide-eyebrow-${si}`} /></Field>
                        <Field label="Heading"><Input value={sl.heading || ""} onChange={(e) => mutate((s) => { s[selected].content.slides[si].heading = e.target.value; })} data-testid={`slide-heading-${si}`} /></Field>
                        <Field label="Body"><RichTextField value={sl.body || ""} onChange={(v) => mutate((s) => { s[selected].content.slides[si].body = v; })} testid={`slide-body-${si}`} /></Field>
                        <div className="grid gap-2 sm:grid-cols-2">
                          <Field label="Background image URL"><Input value={sl.media?.image_url || ""} onChange={(e) => mutate((s) => { s[selected].content.slides[si].media = { ...(s[selected].content.slides[si].media || {}), image_url: e.target.value }; })} data-testid={`slide-image-${si}`} placeholder="https://… or /api/media/file/…" /></Field>
                          <Field label="Background video URL (optional)"><Input value={sl.media?.video_url || ""} onChange={(e) => mutate((s) => { s[selected].content.slides[si].media = { ...(s[selected].content.slides[si].media || {}), video_url: e.target.value }; })} data-testid={`slide-video-${si}`} placeholder=".webm / .mp4" /></Field>
                        </div>
                        {sl.media?.image_url && <img src={sl.media.image_url} alt="" className="h-24 w-full rounded object-cover" />}
                        <div className="grid gap-2 sm:grid-cols-2">
                          <Field label="Overlay">
                            <Select value={ov.type || "red"} onValueChange={(v) => mutate((s) => { s[selected].content.slides[si].overlay = { ...ov, type: v }; })}>
                              <SelectTrigger data-testid={`slide-overlay-${si}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="red">Red overlay (default)</SelectItem>
                                <SelectItem value="black">Black (adjustable opacity)</SelectItem>
                              </SelectContent>
                            </Select>
                          </Field>
                          {(ov.type || "red") === "black" && (
                            <Field label={`Black opacity — ${ov.opacity ?? 55}%`}>
                              <Slider value={[ov.opacity ?? 55]} min={0} max={100} step={5} onValueChange={([v]) => mutate((s) => { s[selected].content.slides[si].overlay = { ...ov, opacity: v }; })} data-testid={`slide-opacity-${si}`} />
                            </Field>
                          )}
                        </div>
                        <div className="space-y-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Buttons (max 2)</p>
                          {btns.map((b, bi) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <div key={bi} className="grid gap-2 rounded-md border border-border p-2 sm:grid-cols-3" data-testid={`slide-${si}-btn-${bi}`}>
                              <Field label="Label"><Input value={b.label || ""} onChange={(e) => mutate((s) => { s[selected].content.slides[si].buttons[bi].label = e.target.value; })} data-testid={`slide-${si}-btn-label-${bi}`} /></Field>
                              <Field label="Link"><Input value={b.href || ""} onChange={(e) => mutate((s) => { s[selected].content.slides[si].buttons[bi].href = e.target.value; })} data-testid={`slide-${si}-btn-href-${bi}`} /></Field>
                              <Field label="Style">
                                <Select value={b.style || "light"} onValueChange={(v) => mutate((s) => { s[selected].content.slides[si].buttons[bi].style = v; })}>
                                  <SelectTrigger data-testid={`slide-${si}-btn-style-${bi}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>{BTN_STYLES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}<SelectItem value="dark">Dark (Black)</SelectItem></SelectContent>
                                </Select>
                              </Field>
                              <button onClick={() => mutate((s) => { s[selected].content.slides[si].buttons.splice(bi, 1); })} data-testid={`slide-${si}-btn-remove-${bi}`} className="text-xs font-semibold uppercase text-destructive hover:underline sm:col-span-3">Remove button</button>
                            </div>
                          ))}
                          {btns.length < 2 && (
                            <button onClick={() => mutate((s) => { const sl2 = s[selected].content.slides[si]; sl2.buttons = [...(sl2.buttons || []), { label: "Learn More", href: "/", style: "secondary" }]; })} data-testid={`slide-${si}-btn-add`} className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold uppercase hover:bg-accent"><Plus className="h-3.5 w-3.5" /> Add button</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <button onClick={() => mutate((s) => { s[selected].content.slides = [...(s[selected].content.slides || []), newCarouselSlide()]; })} data-testid="slide-add" className="inline-flex items-center gap-1.5 rounded-md bg-brand px-4 py-2 text-xs font-semibold uppercase text-white transition-colors hover:bg-[hsl(var(--brand-red-deep))]"><Plus className="h-4 w-4" /> Add slide</button>
                </TabsContent>
              )}

              {/* MEDIA */}
              <TabsContent value="media" className="mt-4 space-y-4">
                {sec.media?.image_url ? (
                  <div className="relative overflow-hidden rounded-lg border border-border">
                    <img src={sec.media.image_url} alt={sec.media.alt || ""} className="h-44 w-full object-cover" style={{ objectPosition: `${sec.media.focal_x ?? 50}% ${sec.media.focal_y ?? 50}%` }} data-testid="media-preview" />
                  </div>
                ) : <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-border text-sm text-muted-foreground">No image set</div>}
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setPickerOpen(true)} data-testid="media-choose-btn" className="inline-flex items-center gap-1.5 rounded-md bg-brand px-4 py-2 text-xs font-semibold uppercase text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors"><ImageIcon className="h-4 w-4" /> Choose / Upload Image</button>
                  {sec.media?.image_url && <button onClick={() => patchNested(selected, "media", { image_url: "", id: "" })} className="inline-flex items-center gap-1.5 rounded-md border border-border px-4 py-2 text-xs font-semibold uppercase hover:bg-accent transition-colors">Remove</button>}
                </div>
                <Field label="Image URL (or paste an external URL)"><Input value={sec.media?.image_url || ""} onChange={(e) => patchNested(selected, "media", { image_url: e.target.value })} data-testid="media-url" placeholder="https://… or /api/media/file/…" /></Field>
                <Field label="Alt text (accessibility)"><Input value={sec.media?.alt || ""} onChange={(e) => patchNested(selected, "media", { alt: e.target.value })} data-testid="media-alt" placeholder="Describe the image" /></Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={`Focal point X — ${sec.media?.focal_x ?? 50}%`}>
                    <Slider value={[sec.media?.focal_x ?? 50]} min={0} max={100} step={1} onValueChange={([v]) => patchNested(selected, "media", { focal_x: v })} data-testid="media-focal-x" />
                  </Field>
                  <Field label={`Focal point Y — ${sec.media?.focal_y ?? 50}%`}>
                    <Slider value={[sec.media?.focal_y ?? 50]} min={0} max={100} step={1} onValueChange={([v]) => patchNested(selected, "media", { focal_y: v })} data-testid="media-focal-y" />
                  </Field>
                </div>
                <Field label="Background video URL (optional, .webm/.mp4)"><Input value={sec.media?.video_url || ""} onChange={(e) => patchNested(selected, "media", { video_url: e.target.value })} data-testid="media-video-url" /></Field>
              </TabsContent>

              {/* OVERLAY */}
              <TabsContent value="overlay" className="mt-4 space-y-4">
                <Field label="Overlay style">
                  <Select value={sec.overlay?.type || "none"} onValueChange={(v) => patchNested(selected, "overlay", { type: v })}>
                    <SelectTrigger data-testid="overlay-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{OVERLAY_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label={`Opacity — ${sec.overlay?.opacity ?? 0}%`}>
                  <Slider value={[sec.overlay?.opacity ?? 0]} min={0} max={100} step={1} onValueChange={([v]) => patchNested(selected, "overlay", { opacity: v })} data-testid="overlay-opacity" disabled={(sec.overlay?.type || "none") === "none"} />
                </Field>
                {/* live preview */}
                <div className="relative h-32 overflow-hidden rounded-lg border border-border bg-muted">
                  {sec.media?.image_url && <img src={sec.media.image_url} alt="" className="absolute inset-0 h-full w-full object-cover" />}
                  <div className="absolute inset-0" style={overlayStyle(sec.overlay?.type || "none", sec.overlay?.opacity)} />
                  <span className="absolute bottom-2 left-2 rounded bg-black/50 px-2 py-0.5 text-[11px] text-white">Overlay preview</span>
                </div>
              </TabsContent>

              {/* BUTTONS */}
              <TabsContent value="buttons" className="mt-4 space-y-4">
                {(sec.buttons || []).map((b, bi) => (
                  <div key={b.id ?? bi} className="rounded-lg border border-border p-3" data-testid={`button-row-${bi}`}>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field label="Label"><Input value={b.label || ""} onChange={(e) => mutate((s) => { s[selected].buttons[bi].label = e.target.value; })} data-testid={`button-label-${bi}`} /></Field>
                      <Field label="Link (href)"><Input value={b.href || ""} onChange={(e) => mutate((s) => { s[selected].buttons[bi].href = e.target.value; })} data-testid={`button-href-${bi}`} /></Field>
                      <Field label="Style">
                        <Select value={b.style || "primary"} onValueChange={(v) => mutate((s) => { s[selected].buttons[bi].style = v; })}>
                          <SelectTrigger data-testid={`button-style-${bi}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{BTN_STYLES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </Field>
                      <div className="flex items-end gap-3">
                        <label className="flex items-center gap-2 text-sm"><Switch checked={!!b.external} onCheckedChange={(v) => mutate((s) => { s[selected].buttons[bi].external = v; })} /> Opens new tab</label>
                        <button onClick={() => mutate((s) => s[selected].buttons.splice(bi, 1))} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold uppercase text-destructive hover:underline" data-testid={`button-remove-${bi}`}><Trash2 className="h-3.5 w-3.5" /> Remove</button>
                      </div>
                    </div>
                  </div>
                ))}
                <button onClick={() => mutate((s) => { if (!s[selected].buttons) s[selected].buttons = []; s[selected].buttons.push({ id: uid(), label: "New Button", href: "#", style: "primary", external: false }); })} data-testid="button-add" className="inline-flex items-center gap-1.5 rounded-md border border-border px-4 py-2 text-xs font-semibold uppercase hover:bg-accent transition-colors"><Plus className="h-4 w-4" /> Add Button</button>
              </TabsContent>

              {/* VISIBILITY */}
              <TabsContent value="visibility" className="mt-4 space-y-4">
                <div className="flex items-center gap-3">
                  <Switch checked={sec.visible !== false} onCheckedChange={(v) => patchSection(selected, { visible: v })} data-testid="visibility-switch" />
                  <span className="text-sm">{sec.visible === false ? "Hidden from visitors (still saved)" : "Visible to visitors"}</span>
                </div>
                <p className="text-xs text-muted-foreground">Hidden sections keep their content and order; they simply aren't shown on the live page.</p>
              </TabsContent>
            </Tabs>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Media picker */}
      <MediaPicker open={pickerOpen} onOpenChange={setPickerOpen} onSelect={(m) => patchNested(selected, "media", { image_url: m.url, alt: sec?.media?.alt || m.alt, id: m.id })} />

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-5xl" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-4">
              <span className="display-title text-2xl">Draft Preview</span>
              <div className="mr-6 flex items-center gap-1 rounded-md border border-border p-1">
                <button onClick={() => setViewport("phone")} data-testid="preview-phone" className={`flex h-8 w-8 items-center justify-center rounded ${viewport === "phone" ? "bg-brand text-white" : "hover:bg-accent"}`}><Smartphone className="h-4 w-4" /></button>
                <button onClick={() => setViewport("tablet")} data-testid="preview-tablet" className={`flex h-8 w-8 items-center justify-center rounded ${viewport === "tablet" ? "bg-brand text-white" : "hover:bg-accent"}`}><Tablet className="h-4 w-4" /></button>
                <button onClick={() => setViewport("desktop")} data-testid="preview-desktop" className={`flex h-8 w-8 items-center justify-center rounded ${viewport === "desktop" ? "bg-brand text-white" : "hover:bg-accent"}`}><Monitor className="h-4 w-4" /></button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-auto rounded-lg border border-border bg-background">
            <div className="mx-auto" style={{ width: VIEWPORTS[viewport], maxWidth: "100%" }} data-testid="preview-canvas">
              {previewSections.map((s) => <SectionRenderer key={s.id} section={s} preview />)}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Revisions dialog */}
      <Dialog open={revisionsOpen} onOpenChange={setRevisionsOpen}>
        <DialogContent data-testid="revisions-dialog">
          <DialogHeader><DialogTitle className="display-title text-2xl">Revision History</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {revisions.length === 0 ? <p className="text-sm text-muted-foreground">No revisions yet. Commit changes to create one.</p> : revisions.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg border border-border p-3" data-testid={`revision-${r.version}`}>
                <div>
                  <p className="text-sm font-medium">Version {r.version} · {r.section_count} sections</p>
                  <p className="text-xs text-muted-foreground">{r.committed_by} · {r.committed_at ? new Date(r.committed_at).toLocaleString() : ""}</p>
                </div>
                {isAdmin && <button onClick={() => restore(r.id)} data-testid={`revision-restore-${r.version}`} className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold uppercase hover:bg-accent transition-colors"><RotateCcw className="h-3.5 w-3.5" /> Restore</button>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

// Editable, url-safe, unique-per-page anchor id used for deep links (e.g. /#home-app).
const AnchorField = ({ value, duplicate, onChange, onBlur }) => (
  <Field label="Anchor ID (URL #link)">
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={(e) => (onBlur || onChange)(e.target.value)}
      placeholder="e.g. home-app"
      data-testid="section-anchor"
      className={duplicate ? "border-destructive focus-visible:ring-destructive" : ""}
    />
    <p className={`mt-1 text-xs ${duplicate ? "text-destructive" : "text-muted-foreground"}`}>
      {duplicate ? "Duplicate anchor on this page — each must be unique." : "Deep link target, e.g. /#home-app. Lowercase letters, numbers and dashes only."}
    </p>
  </Field>
);

// Editor for an embed widget's repeatable card list (content.items). Supports
// per-card image upload (drag/drop/library), text fields, reorder and remove.
const EmbedItemsEditor = ({ spec, items, mutate, selected }) => {
  const list = Array.isArray(items) ? items : [];
  const setItem = (i, key, value) => mutate((s) => {
    const arr = [...((s[selected].content && s[selected].content.items) || [])];
    arr[i] = { ...arr[i], [key]: value };
    s[selected].content = { ...(s[selected].content || {}), items: arr };
  });
  const removeItem = (i) => mutate((s) => {
    const arr = [...((s[selected].content && s[selected].content.items) || [])];
    arr.splice(i, 1);
    s[selected].content = { ...(s[selected].content || {}), items: arr };
  });
  const moveItem = (i, dir) => mutate((s) => {
    const arr = [...((s[selected].content && s[selected].content.items) || [])];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    s[selected].content = { ...(s[selected].content || {}), items: arr };
  });
  const addItem = () => mutate((s) => {
    const arr = [...((s[selected].content && s[selected].content.items) || [])];
    arr.push({ ...spec.defaults });
    s[selected].content = { ...(s[selected].content || {}), items: arr };
  });
  return (
    <div className="mt-3 rounded-lg border border-border p-4" data-testid="embed-items-editor">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand">{spec.label} — cards</p>
      <p className="mb-4 text-xs text-muted-foreground">Add, edit, reorder or remove cards. Images support upload, drag &amp; drop, library and AI. Leave the list empty to show the built-in defaults.</p>
      <div className="space-y-3">
        {list.map((it, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="space-y-2 rounded-lg border border-border p-3" data-testid={`widget-item-row-${i}`}>
            <div className="flex items-center justify-between">
              <p className="font-condensed text-sm font-semibold uppercase tracking-wide">{spec.label} {i + 1}</p>
              <div className="flex items-center gap-2">
                <button onClick={() => moveItem(i, -1)} disabled={i === 0} data-testid={`widget-item-up-${i}`} className="text-xs font-semibold uppercase text-muted-foreground hover:text-foreground disabled:opacity-40">↑ Up</button>
                <button onClick={() => moveItem(i, 1)} disabled={i === list.length - 1} data-testid={`widget-item-down-${i}`} className="text-xs font-semibold uppercase text-muted-foreground hover:text-foreground disabled:opacity-40">↓ Down</button>
                <button onClick={() => removeItem(i)} data-testid={`widget-item-remove-${i}`} className="text-xs font-semibold uppercase text-destructive hover:underline">Remove</button>
              </div>
            </div>
            {spec.fields.map((f) => {
              if (f.type === "image") {
                return (
                  <div key={f.name}>
                    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{f.label}</Label>
                    <AdminImageInput value={it[f.name] || ""} onChange={(v) => setItem(i, f.name, v)} testid={`widget-item-${i}-${f.name}`} />
                  </div>
                );
              }
              if (f.type === "textarea") {
                return <Field key={f.name} label={f.label}><Textarea rows={2} value={it[f.name] || ""} onChange={(e) => setItem(i, f.name, e.target.value)} data-testid={`widget-item-${i}-${f.name}`} placeholder={f.placeholder} /></Field>;
              }
              return <Field key={f.name} label={f.label}><Input value={it[f.name] || ""} onChange={(e) => setItem(i, f.name, e.target.value)} data-testid={`widget-item-${i}-${f.name}`} placeholder={f.placeholder} /></Field>;
            })}
          </div>
        ))}
      </div>
      <button onClick={addItem} data-testid="widget-item-add" className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-brand px-4 py-2 text-xs font-semibold uppercase text-white transition-colors hover:bg-[hsl(var(--brand-red-deep))]"><Plus className="h-4 w-4" /> {spec.addLabel}</button>
    </div>
  );
};


// Generic editor for functional/embed widget "chrome" (headings, copy, images,
// bullet lists, link cards). Driven by the per-widget schema in widgetEditable.js.
const EmbedChromeEditor = ({ fields, content, setField }) => (
  <div className="space-y-4">
    {fields.map((f) => {
      const val = content[f.name];
      if (f.type === "list") {
        const items = Array.isArray(val) ? val : [];
        return (
          <Field key={f.name} label={f.label}>
            <div className="space-y-2">
              {items.map((it, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} className="flex items-center gap-2">
                  <Input value={it} onChange={(e) => setField(f.name, items.map((x, xi) => (xi === i ? e.target.value : x)))} data-testid={`chrome-${f.name}-item-${i}`} />
                  <button onClick={() => setField(f.name, items.filter((_, xi) => xi !== i))} data-testid={`chrome-${f.name}-remove-${i}`} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <button onClick={() => setField(f.name, [...items, ""])} data-testid={`chrome-${f.name}-add`} className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase hover:bg-accent"><Plus className="h-3.5 w-3.5" /> Add item</button>
            </div>
            {f.help && <p className="mt-1.5 text-xs text-muted-foreground">{f.help}</p>}
          </Field>
        );
      }
      if (f.type === "links") {
        const items = Array.isArray(val) ? val : [];
        return (
          <Field key={f.name} label={f.label}>
            <div className="space-y-2">
              {items.map((lnk, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                  <Input value={lnk.label || ""} onChange={(e) => setField(f.name, items.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))} placeholder="Label" data-testid={`chrome-${f.name}-label-${i}`} />
                  <Input value={lnk.to || ""} onChange={(e) => setField(f.name, items.map((x, xi) => (xi === i ? { ...x, to: e.target.value } : x)))} placeholder="/path or https://…" data-testid={`chrome-${f.name}-to-${i}`} />
                  <button onClick={() => setField(f.name, items.filter((_, xi) => xi !== i))} data-testid={`chrome-${f.name}-remove-${i}`} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <button onClick={() => setField(f.name, [...items, { label: "New link", to: "/" }])} data-testid={`chrome-${f.name}-add`} className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase hover:bg-accent"><Plus className="h-3.5 w-3.5" /> Add link</button>
            </div>
          </Field>
        );
      }
      return (
        <Field key={f.name} label={f.label}>
          {f.type === "textarea" ? (
            <Textarea rows={3} value={val || ""} placeholder={f.placeholder} onChange={(e) => setField(f.name, e.target.value)} data-testid={`chrome-${f.name}`} />
          ) : (
            <Input value={val || ""} placeholder={f.placeholder} onChange={(e) => setField(f.name, e.target.value)} data-testid={`chrome-${f.name}`} />
          )}
          {f.type === "image" && val && <img src={val} alt="" className="mt-2 h-24 w-auto rounded object-contain" />}
          {f.help && <p className="mt-1.5 text-xs text-muted-foreground">{f.help}</p>}
        </Field>
      );
    })}
  </div>
);
